import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import ErrorMessage from '@/components/ErrorMessage';
import { useSignupBasicDetailsForm } from '@/redux/auth/useAuthForm';
import { IUserSignupDataWithConfirmPassword } from '@/pages/Signup/signup.types';
import { AuthProvider, IUser } from '@/redux/auth/auth.d';
import { debounce } from '@/lib/utils';
import { useLazyCheckUsernameAvailabilityQuery } from '@/redux/auth/auth.api.slice';
import { ProfilePhotoInput } from '@/components/ui/profile-photo-input';
import {
  PURCHASE_EMAIL_REQUIRED_MESSAGE,
  usernameSchema,
} from '@/redux/auth/auth.schema';

const SECONDHAND_FAQ_URL =
  'https://forum.onefinitycnc.com/t/how-do-i-get-filefinity-access-if-i-bought-my-onefinity-secondhand/40299';

function BasicDetails({
  user,
  formData,
  errors,
  setFormData,
}: {
  user: IUser | null;
  formData: IUserSignupDataWithConfirmPassword;
  errors: { [key: string]: string };
  setFormData: Dispatch<SetStateAction<IUserSignupDataWithConfirmPassword>>;
}) {
  const IS_USER_SINGUP_COMPLETE: boolean = Boolean(user?.isUserSignupComplete);
  const IS_SOCIAL_LOGIN: boolean = user?.authProvider !== AuthProvider.Email;

  const [isUsernameAvailable, setIsUsernameAvailable] = useState<string | null>(
    null
  );
  const [tempUsername, setTempUsername] = useState<string>(
    formData.username || ''
  );

  const signupBaiscDetailsForm = useSignupBasicDetailsForm(
    !(Boolean(user) && IS_SOCIAL_LOGIN && !IS_USER_SINGUP_COMPLETE)
  );

  const [checkUsernameAvailabilityInDb] =
    useLazyCheckUsernameAvailabilityQuery();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const checkUsernameAvailability = useCallback(
    debounce(async (username: string) => {
      if (username.length < 4) {
        setIsUsernameAvailable('');
        return;
      }

      try {
        usernameSchema.parse(username);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        setIsUsernameAvailable(error.errors[0].message);
        return;
      }

      setIsUsernameAvailable('checking');
      const { data: isAvailable } =
        await checkUsernameAvailabilityInDb(username);
      if (isAvailable) {
        setIsUsernameAvailable('available');
      } else {
        setIsUsernameAvailable('unavailable');
      }
    }, 300),
    []
  );

  const handleInputChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    fieldOnChange: (e: any) => void
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    fieldOnChange(e);
  };

  const handleUsernameChange = (
    e: ChangeEvent<HTMLInputElement>,
    fieldOnChange: (value: string) => void
  ) => {
    const { value } = e.target;
    setTempUsername(value);
    setFormData((prev) => ({ ...prev, username: value }));
    fieldOnChange(value);
    checkUsernameAvailability(value);
  };

  useEffect(() => {
    if (formData.username && formData.username !== tempUsername) {
      setTempUsername(formData.username);
    }
  }, [formData.username, tempUsername]);

  useEffect(() => {
    const currentValues = signupBaiscDetailsForm.getValues();
    const passwordsMatch =
      'password' in currentValues && 'confirmPassword' in currentValues
        ? currentValues.password === formData.password &&
          currentValues.confirmPassword === formData.confirmPassword
        : true;

    const isSame =
      currentValues.firstname === formData.firstname &&
      currentValues.lastname === formData.lastname &&
      currentValues.email === formData.email &&
      currentValues.username === formData.username &&
      passwordsMatch;

    if (isSame) return;

    const formDataForReset = {
      ...formData,
      profilePicture:
        typeof formData.profilePicture === 'string'
          ? formData.profilePicture
          : undefined,
    };
    signupBaiscDetailsForm.reset(formDataForReset);
  }, [formData, signupBaiscDetailsForm]);

  const handlePhotoChange = (file: File | null) => {
    if (file) {
      setFormData((prev) => ({ ...prev, profilePicture: file }));
    } else {
      setFormData((prev) => ({ ...prev, profilePicture: '' }));
    }
  };

  return (
    <Form {...signupBaiscDetailsForm}>
      <form autoComplete="off" className="space-y-7">
        {/* Profile photo — left aligned */}
        <div className="flex items-center gap-4">
          <FormField
            control={signupBaiscDetailsForm.control}
            name="profilePicture"
            render={({ field }) => (
              <FormItem className="space-y-0">
                <FormControl>
                  <ProfilePhotoInput
                    {...field}
                    value={formData.profilePicture}
                    onChange={handlePhotoChange}
                    className="h-28 w-28"
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div>
            <p className="text-sm font-medium text-foreground">Profile photo</p>
            <p className="text-xs text-muted-foreground">
              Optional — add a face to your profile.
            </p>
            <ErrorMessage error={errors.profilePicture} />
          </div>
        </div>

        <div className="space-y-5">
          {/* Firstname | Lastname */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={signupBaiscDetailsForm.control}
              name="firstname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input
                      className="h-11"
                      autoComplete="name"
                      placeholder="Jane"
                      {...field}
                      onChange={(e) => handleInputChange(e, field.onChange)}
                    />
                  </FormControl>
                  <ErrorMessage error={errors.firstname} />
                </FormItem>
              )}
            />
            <FormField
              control={signupBaiscDetailsForm.control}
              name="lastname"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input
                      className="h-11"
                      autoComplete="family-name"
                      placeholder="Maker"
                      {...field}
                      onChange={(e) => handleInputChange(e, field.onChange)}
                    />
                  </FormControl>
                  <ErrorMessage error={errors.lastname} />
                </FormItem>
              )}
            />
          </div>

          {/* Username */}
          <FormField
            control={signupBaiscDetailsForm.control}
            name="username"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      className="h-11 pr-10"
                      // Not "username": that token names the saved credential,
                      // and login authenticates on email.
                      autoComplete="nickname"
                      placeholder="janemaker"
                      {...field}
                      value={tempUsername}
                      onChange={(e) => handleUsernameChange(e, field.onChange)}
                    />
                    {isUsernameAvailable && (
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                        {isUsernameAvailable === 'checking' && (
                          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                        )}
                        {isUsernameAvailable === 'available' && (
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                        )}
                        {isUsernameAvailable !== 'available' &&
                          isUsernameAvailable !== 'checking' && (
                            <XCircle className="h-5 w-5 text-red-500" />
                          )}
                      </div>
                    )}
                  </div>
                </FormControl>
                <ErrorMessage error={errors.username} />
                {isUsernameAvailable !== 'available' &&
                  isUsernameAvailable !== 'checking' && (
                    <p className="text-sm text-red-600">
                      {isUsernameAvailable}
                    </p>
                  )}
              </FormItem>
            )}
          />

          {/* Email */}
          <FormField
            control={signupBaiscDetailsForm.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    className="h-11"
                    type="email"
                    // Names the identifier saved beside the password; login
                    // authenticates on email, not the handle above.
                    autoComplete="username"
                    placeholder="you@example.com"
                    // This error lives outside react-hook-form, so FormControl
                    // does not describe the field with it.
                    aria-describedby={
                      errors.email ? 'signup-email-error' : 'signup-email-hint'
                    }
                    {...field}
                    onChange={(e) => handleInputChange(e, field.onChange)}
                  />
                </FormControl>
                {errors.email ? (
                  <ErrorMessage id="signup-email-error" error={errors.email} />
                ) : (
                  <p
                    id="signup-email-hint"
                    className="text-sm text-muted-foreground"
                  >
                    {PURCHASE_EMAIL_REQUIRED_MESSAGE}
                  </p>
                )}
                <a
                  href={SECONDHAND_FAQ_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block text-sm font-semibold text-green-700 underline-offset-2 hover:text-green-600 hover:underline"
                >
                  Bought your Onefinity secondhand?
                </a>
              </FormItem>
            )}
          />

          {/* Passwords */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField
              control={signupBaiscDetailsForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Password</FormLabel>
                  <FormControl>
                    <Input
                      className="h-11"
                      autoComplete="new-password"
                      type="password"
                      placeholder="At least 8 characters"
                      {...field}
                      onChange={(e) => handleInputChange(e, field.onChange)}
                    />
                  </FormControl>
                  <ErrorMessage error={errors.password} />
                </FormItem>
              )}
            />
            <FormField
              control={signupBaiscDetailsForm.control}
              name="confirmPassword"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm password</FormLabel>
                  <FormControl>
                    <Input
                      className="h-11"
                      autoComplete="new-password"
                      type="password"
                      placeholder="Re-enter your password"
                      {...field}
                      onChange={(e) => handleInputChange(e, field.onChange)}
                    />
                  </FormControl>
                  <ErrorMessage error={errors.confirmPassword} />
                </FormItem>
              )}
            />
          </div>
        </div>
      </form>
    </Form>
  );
}

export default BasicDetails;
